import axios from 'axios';

// Import attack-related types
import {
  AttackType,
  AttackTypeCreateRequest,
  AttackTypeUpdateRequest,
  Scenario,
  Execution,
  ScenarioCreateRequest,
  ScenarioUpdateRequest,
  ExecutionCreateRequest,
  ExecutionBotIp,
  CurrentScenarioResponse,
  ScenarioS3ConfigPayload,
  ScenarioDebugState,
  TestRunState
} from '../types/attacks';

// Import provider-related types
import {
  Provider,
  InstanceCatalog,
  ProviderCreateRequest,
  ProviderUpdateRequest,
  SupportedProvider,
  RegionImageInfo,
  ImageSyncRequest,
  ImageSyncResponse,
  ImageUpdateBulkRequest,
  ImageUpdateResponse,
  SupportedInstanceType,
  RegionCreateRequest,
  RegionUpdateRequest,
  AzureReservationSummary,
  AzureReservationOperation,
  ProviderError,
  ProviderErrorsResponse
} from '../types/providers';

// Import allow list types
import {
  AllowListEntry,
  AllowListCreateRequest,
  AllowListUpdateRequest,
  AllowListStats,
  AllowListBulkDeleteResponse
} from '../types/allowList';
import type { AssetDetailResponse } from '../types/assets';
import {
  AssetEntry,
  AssetDiscovery,
  AssetDiscoveryUpdateRequest,
} from '../types/assets';

// Import runbook types
import {
  Runbook,
  RunbookListItem,
  RunbookCreateRequest,
  RunbookUpdateRequest,
  SlackChannelsResponse
} from '../types/runbooks';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const CAOSBLITZ_HEADER = '9078a4ae-65a0-409a-9bd6-1592d900777b';

const clearAuthSession = () => {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('selectedTenant');
};

const performTokenRefresh = async (): Promise<AuthResponse> => {
  const token = sessionStorage.getItem('authToken');
  if (!token) {
    throw new Error('No auth token available to refresh');
  }

  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-CAOSBLITZ-HEADER': CAOSBLITZ_HEADER
    }
  });

  return response.data;
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token and tenant header
axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant header if available
    const selectedTenant = sessionStorage.getItem('selectedTenant');
    if (selectedTenant) {
      try {
        const tenant = JSON.parse(selectedTenant);
        config.headers['X-Tenant-ID'] = tenant.tenant_id;
      } catch (error) {
        console.error('Failed to parse selected tenant:', error);
      }
    }
    
    // Add CaosBlitz header to all requests
    config.headers['X-CAOSBLITZ-HEADER'] = CAOSBLITZ_HEADER;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: any = error.config || {};
    const requestUrl: string = originalRequest?.url || '';
    const hasToken = !!sessionStorage.getItem('authToken');
    if (error.response?.status === 401 && hasToken && !originalRequest._retry && !requestUrl.includes('/auth/refresh')) {
      originalRequest._retry = true;
      try {
        const refreshResponse = await performTokenRefresh();
        sessionStorage.setItem('authToken', refreshResponse.access_token);
        sessionStorage.setItem('user', JSON.stringify(refreshResponse.user));
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        clearAuthSession();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    if (error.response?.status === 401) {
      clearAuthSession();
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export interface Region {
  launch_config?: { instance_type?: string | null; subnet_id?: string | null; security_group_id?: string | null; key_pair_name?: string | null } | null;
  id?: string;
  name: string;
  region_code: string;
  location?: string;
  continent?: string;
  configured: boolean;
  machine_count: number;
  capacity?: number; // Maximum bot capacity for this region
  status?: string; // Region status from backend: 'available' | 'cooldown' | 'unavailable'
  cooldown_reason?: string | null;
  blocked_reason?: string | null;
  cooldown_until?: string | null;
  status_reason?: string | null;
  error_message?: string | null;
  effective_instance_limit?: number | null;
  effective_instance_available?: number | null;
  /**
   * Total capacity denominator shown as "bots / capacity" in the Regions & Bots panel.
   * Provider-specific, computed by the backend: active warm-pool reservation units for
   * azure/gcp, the quota-based effective instance limit otherwise.
   */
  capacity_total?: number | null;
  provider?: {
    id: string;
    name: string;
    provider_type: 'aws' | 'gcp' | 'azure' | 'openstack';
    identifier: string;
    bot_runtime?: 'vm' | 'docker';
  };
  machine_image_config?: {
    image_id: string;
    image_type: string;
    key_pair_name?: string;
    security_group_name?: string;
  };
}

// Region Capacity Types
export interface RegionPricing {
  currency: 'USD';
  region_code: string;
  instance_type: string | null;
  instance_hourly: number | null;
  egress_per_gb: number | null;
  egress_tiers: { begin_gb: number; end_gb: number | null; price_per_gb: number }[];
  checked_at: string | null;
  source_url: string | null;
  status: 'available' | 'partial' | 'unavailable';
  message: string | null;
}

export interface RegionCapacity {
  region_id: string;
  region_name: string;
  region_code: string;
  status: 'green' | 'yellow' | 'red';
  vcpu_limit: number;
  vcpu_used: number;
  instance_limit: number;
  instance_used: number;
  ip_limit: number;
  ip_used: number;
  manual_bot_limit: number | null;
  cooldown_until: string | null;
  cooldown_reason: string | null;
  last_synced_at: string | null;
  effective_instance_limit?: number | null;
  effective_instance_available?: number | null;
  vcpu_per_bot?: number | null;
  pricing?: RegionPricing | null;
}

export interface CapacitySyncResponse {
  provider_id: string;
  provider_name: string;
  regions_synced: number;
  total_regions: number;
  errors: string[];
  job_id?: string;
  status?: 'running' | 'completed' | 'failed' | 'cancelled';
  message?: string;
  started_at?: string;
  updated_at?: string;
  completed_at?: string | null;
}

export interface RegionSyncStartResponse {
  operation_id: string;
  provider_id: string;
  provider_name: string;
  status: string;
  message: string;
}

export interface RegionSyncOperationResponse {
  warnings?: string[];
  operation_id: string;
  provider_id: string;
  provider_name: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed';
  available_count: number;
  created: string[];   // preview: regions that would be added
  disabled: string[];  // preview: regions that would be disabled
  unchanged_count: number;
  error?: string | null;
  message: string;
}

export interface RegionSyncApplyResponse {
  provider_id: string;
  provider_name: string;
  available_count: number;
  created: string[];
  disabled: string[];
  unchanged_count: number;
  message: string;
}

export interface ManualLimitUpdateResponse {
  region_id: string;
  region_name: string;
  manual_bot_limit: number | null;
  status: string;
}

export interface CooldownResponse {
  region_id: string;
  region_name: string;
  status: string;
  reason: string;
  duration_minutes: number;
}

export type QuotaIncreaseDimension = 'vcpu' | 'ip' | 'instance';

export type QuotaIncreaseStatus =
  | 'submitted'
  | 'in_progress'
  | 'succeeded'
  | 'failed'
  | 'skipped';

export interface QuotaIncreaseItem {
  dimension: QuotaIncreaseDimension;
  quota_name: string | null;
  current_limit: number;
  requested_limit: number;
  status: QuotaIncreaseStatus;
  message: string;
  ticket_id: string | null;
}

export interface QuotaIncreaseResponse {
  provider_id: string;
  provider_type: string;
  region_id: string;
  region_code: string;
  multiplier: number;
  requests: QuotaIncreaseItem[];
}

export interface BotStats {
  unique_bots: number;
  requests: number;
  rps: number;
  avg_response_time: number;
  status_codes_ok: number;
  status_codes_bad: number;
  errors: number;
  tcp: number;
  avg_tcp_time: number;
  ssl: number;
  avg_ssl_time: number;
  bandwidth_outbound: number;
  bandwidth_inbound: number;
  packets_outbound: number;
  packets_inbound: number;
}

export interface BotListResponse {
  total_running: number;
  total_shutdown: number;
  total_unrelated: number;
  regions: Record<string, { machine_count: number }>;
}

// A single bot row as stored in the database (returned by GET /bots/all).
// This is the ground-truth inventory, independent of Prometheus telemetry.
export interface DbBot {
  id: string;
  status: string;
  region_id: string | null;
  public_ip: string | null;
  tenant_id: string | null;
  scenario_id: string | null;
  instance_id: string | null;
  retry_count: number | null;
  transaction_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  tenant_name: string | null;
  region_name: string | null;
  scenario_name: string | null;
  provider_id: string | null;
  provider_type: string | null;
  region_code: string | null;
}

export interface AllBotsResponse {
  total_bots: number;
  bots_by_status: Record<string, DbBot[]>;
  bots: DbBot[];
  summary: Record<string, number>;
}

export interface BotRegionStatus {
  provider_id: string | null;
  provider_type: string | null;
  provider_name: string | null;
  region_id: string | null;
  region_name: string;
  pending_count: number;
  running_count: number;
  shutting_down_count: number;
  stopped_count: number;
  terminating_count: number;
  terminated_count: number;
  total_count: number;
}

export interface CreateBotRegionSelection {
  region_id: string;
  provider_id?: string;
  region_name?: string;
}

export interface BotStatusResponse {
  total_pending: number;
  total_running: number;
  total_shutting_down: number;
  total_stopped: number;
  total_terminating: number;
  total_terminated: number;
  total_active: number;
  total_inactive: number;
  total_in_process: number;
  regions?: BotRegionStatus[] | Record<string, BotRegionStatus> | null;
  last_updated?: string | null;
}

export const normalizeBotRegions = (
  regions: BotStatusResponse['regions']
): BotRegionStatus[] => {
  if (!regions) {
    return [];
  }
  if (Array.isArray(regions)) {
    return regions;
  }
  return Object.values(regions);
};

export interface Partner {
  id: string;
  name: string;
}

export interface Tenant {
  id: string;
  name: string;
  partner_id?: string;
}

export interface AuditLogEntry {
  id: string;
  event_type: string;
  action: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  timestamp: string | null;
  ip_address: string | null;
  user_agent: string | null;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
}

export interface PartnerCreateRequest {
  name: string;
}

export interface PartnerUpdateRequest {
  name?: string;
}

export interface TenantCreateRequest {
  name: string;
  partner_id?: string;
}

export interface TenantUpdateRequest {
  name?: string;
  partner_id?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  is_admin: boolean;
  auth_provider?: 'google' | 'microsoft';
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface UserPermissions {
  user: User;
  partner_permissions: Array<{
    partner_id: string;
    partner_name: string;
    role: 'viewer' | 'operator' | 'admin';
  }>;
  tenant_permissions: Array<{
    tenant_id: string;
    tenant_name: string;
    partner_id: string;
    partner_name: string;
    role: 'viewer' | 'operator' | 'admin';
  }>;
}

// What backend/environment_policy.py decided for this deployment: which resources a
// tenant deletion here must always perform, and which of them belong to the shared
// production cloud account and so may be declined outside production.
export interface TenantDeletionEnvironment {
  name: string;
  is_production: boolean;
  shared_with_production: string[];
  mandatory_resources: string[];
}

export interface TenantResourceCounts {
  bots: number;
  bot_instances: number;
  scenarios: number;
  executions: number;
  allow_list: number;
  providers: number;
  user_permissions: number;
  validation: {
    has_running_executions: boolean;
    running_executions_count: number;
    has_active_scenarios: boolean;
    active_scenarios_count: number;
    has_s3_scenario: boolean;
    // Outside production that bucket is production's, so its contents are reported but
    // do not block the deletion.
    s3_scenario_blocks_delete?: boolean;
    can_delete: boolean;
  };
  // Absent from an older API, in which case production's rules apply.
  environment?: TenantDeletionEnvironment;
}

export interface UserWithPermissions {
  id: string;
  email: string;
  name: string;
  is_admin: boolean;
  is_active: boolean;
  partner_permissions: Array<{
    partner_id: string;
    partner_name: string;
    role: 'viewer' | 'operator' | 'admin';
  }>;
  tenant_permissions: Array<{
    tenant_id: string;
    tenant_name: string;
    partner_id: string;
    partner_name: string;
    role: 'viewer' | 'operator' | 'admin';
    permission_type: 'direct' | 'inherited';
  }>;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  is_admin: boolean;
}

export interface InviteUserRequest {
  email: string;
  role: string;
  message?: string;
}



export interface Invitation {
  id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  is_accepted: boolean;
  partner_id?: string;
  tenant_id?: string;
}

// ---------------------------------------------------------------------------
// Shared bot-status stream multiplexer
//
// Previously every consumer (ActiveAttackBar, BotStatusDisplay, Operations)
// opened its OWN `streamBotStatus` SSE connection. Over HTTP/1.1 the browser
// caps concurrent connections per host at 6, so 3-4 permanent SSE streams
// starved every other XHR (creates, polling) — requests sat "(pending)" in the
// browser waiting for a free connection slot. This multiplexer keeps a SINGLE
// underlying SSE connection (reference-counted) and fans its payloads out to
// all subscribers, freeing the extra connection slots.
// ---------------------------------------------------------------------------
type BotStatusSubscriber = {
  onData: (data: any) => void;
  onError?: (error: any) => void;
};

let _botStatusSubscribers: BotStatusSubscriber[] = [];
let _botStatusCleanup: (() => void) | null = null;
let _botStatusLast: any = null;
let _botStatusTenantId: string | null = null;

const _currentTenantId = (): string | null => {
  const selectedTenant = sessionStorage.getItem('selectedTenant');
  if (!selectedTenant) return null;
  try {
    return JSON.parse(selectedTenant).tenant_id ?? null;
  } catch {
    return null;
  }
};

const _startSharedBotStatusStream = () => {
  const tenantId = _currentTenantId();

  // Tenant switched: tear down the stale stream so we re-open scoped to the
  // new tenant.
  if (_botStatusCleanup && tenantId !== _botStatusTenantId) {
    try { _botStatusCleanup(); } catch { /* ignore */ }
    _botStatusCleanup = null;
    _botStatusLast = null;
  }

  if (_botStatusCleanup) return; // already streaming for this tenant

  _botStatusTenantId = tenantId;
  _botStatusCleanup = api.streamBotStatus(
    (data) => {
      _botStatusLast = data;
      // Snapshot to tolerate unsubscribe during iteration.
      for (const sub of [..._botStatusSubscribers]) {
        try { sub.onData(data); } catch { /* subscriber threw; ignore */ }
      }
    },
    (error) => {
      for (const sub of [..._botStatusSubscribers]) {
        try { sub.onError?.(error); } catch { /* ignore */ }
      }
    }
  );
};

const api = {
  listResearchJobs: async (tenantId: string): Promise<{ configured: boolean; model: string; jobs: import('../types/research').ResearchJob[] }> =>
    (await axiosInstance.get('/research/jobs', { headers: { 'X-Tenant-ID': tenantId } })).data,
  getResearchModels: async (tenantId: string, refresh = false): Promise<import('../types/research').ResearchModels> =>
    (await axiosInstance.get('/research/jobs/models', { headers: { 'X-Tenant-ID': tenantId }, params: { refresh } })).data,
  getResearchOptions: async (tenantId: string): Promise<import('../types/research').ResearchOptions> =>
    (await axiosInstance.get('/research/jobs/options', { headers: { 'X-Tenant-ID': tenantId } })).data,
  getResearchModelSettings: async (refresh = false): Promise<import('../types/research').ResearchModelSettings> =>
    (await axiosInstance.get('/research/jobs/settings/models', { params: { refresh } })).data,
  updateResearchModelSettings: async (enabled_model_ids: string[]): Promise<import('../types/research').ResearchModelSettings> =>
    (await axiosInstance.put('/research/jobs/settings/models', { enabled_model_ids })).data,
  getResearchSettings: async (): Promise<import('../types/research').ResearchLimits> =>
    (await axiosInstance.get('/research/jobs/settings')).data,
  updateResearchSettings: async (limits: import('../types/research').ResearchLimits): Promise<import('../types/research').ResearchLimits> =>
    (await axiosInstance.put('/research/jobs/settings', limits)).data,
  startResearch: async (tenantId: string, data: { title: string; targets: string[]; context: string; model: string; country: string; attack_type_ids: string[]; ownership_confirmed: boolean }): Promise<import('../types/research').ResearchJob> =>
    (await axiosInstance.post('/research/jobs', data, { headers: { 'X-Tenant-ID': tenantId } })).data,
  getResearchJob: async (tenantId: string, id: string): Promise<import('../types/research').ResearchJob> =>
    (await axiosInstance.get(`/research/jobs/${encodeURIComponent(id)}`, { headers: { 'X-Tenant-ID': tenantId } })).data,
  cancelResearch: async (tenantId: string, id: string): Promise<import('../types/research').ResearchJob> =>
    (await axiosInstance.post(`/research/jobs/${encodeURIComponent(id)}/cancel`, {}, { headers: { 'X-Tenant-ID': tenantId } })).data,
  exportResearch: async (tenantId: string, id: string, candidates?: number[]): Promise<Blob> =>
    (await axiosInstance.get(`/research/jobs/${encodeURIComponent(id)}/workbook`, { headers: { 'X-Tenant-ID': tenantId }, params: candidates ? { candidates: candidates.join(',') } : undefined, responseType: 'blob' })).data,

  getResearchScenarios: async (tenantId: string, offset = 0): Promise<import('../types/research').ResearchResponse> => {
    const response = await axiosInstance.get('/research/scenarios', {
      headers: { 'X-Tenant-ID': tenantId }, params: { offset, limit: 100 },
    });
    return response.data;
  },
  // Authentication
  googleAuth: async (code: string): Promise<AuthResponse> => {
    const response = await axiosInstance.post('/auth/google', { code });
    return response.data;
  },

  microsoftAuth: async (params: { idToken: string }): Promise<AuthResponse> => {
    const response = await axiosInstance.post('/auth/microsoft', {
      id_token: params.idToken,
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<UserPermissions> => {
    const response = await axiosInstance.get('/auth/me');
    return response.data;
  },

  approveMcpAuthorization: async (request: string): Promise<{ redirect_url: string }> => {
    const response = await axiosInstance.post('/mcp-oauth/approve', { request });
    return response.data;
  },

  refreshToken: performTokenRefresh,

  setTenantSession: async (tenantId: string, previousTenantId?: string | null): Promise<{ message: string; tenant_id: string }> => {
    const response = await axiosInstance.post('/auth/tenant-session', {
      tenant_id: tenantId,
      previous_tenant_id: previousTenantId ?? undefined,
    });
    return response.data;
  },

  logout: () => {
    clearAuthSession();
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!sessionStorage.getItem('authToken');
  },

  // Get stored user info
  getStoredUser: (): User | null => {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  getRegions: async (provider_id?: string): Promise<Region[]> => {
    const params = provider_id ? `?provider_id=${provider_id}` : '';
    const response = await axiosInstance.get(`/regions${params}`);
    return response.data;
  },

  createBots: async (amount: number, regions: CreateBotRegionSelection[], threads: number, provider_id?: string, scenario_id?: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/bots', { amount, regions, threads, provider_id, scenario_id });
    return response.data;
  },

  setAttackCommand: async (command: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/attack/command', { command });
    return response.data;
  },

  pauseAttack: async (): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/attack/pause');
    return response.data;
  },

  shutdownBots: async (regions?: string[]): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/bots/shutdown', regions ? { regions } : undefined);
    return response.data;
  },

  terminateBots: async (regions: string[]): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/bots/terminate', { regions });
    return response.data;
  },

  deleteTerminatedBots: async (gracePeriodMinutes?: number): Promise<{ deleted_count: number; message: string }> => {
    const response = await axiosInstance.delete('/bots/terminated', {
      params: gracePeriodMinutes !== undefined
        ? { grace_period_minutes: gracePeriodMinutes }
        : undefined
    });
    return response.data;
  },

  listBots: async (): Promise<BotListResponse> => {
    const response = await axiosInstance.get('/bots');
    return response.data;
  },

  // Full DB inventory of non-terminated bots (tenant-scoped server-side).
  getAllBots: async (): Promise<AllBotsResponse> => {
    const response = await axiosInstance.get('/bots/all');
    return response.data;
  },

  getDetailedBotStatus: async (providerId?: string): Promise<BotStatusResponse> => {
    const response = await axiosInstance.get('/bots/status', {
      params: providerId ? { provider_id: providerId } : undefined
    });
    return response.data;
  },

  // Subscribe to the shared, single-connection bot-status stream. Prefer this
  // over streamBotStatus() in UI components so we don't open a fresh SSE
  // connection per consumer. Returns an unsubscribe function.
  subscribeBotStatus: (onData: (data: any) => void, onError?: (error: any) => void) => {
    const sub: BotStatusSubscriber = { onData, onError };
    _botStatusSubscribers.push(sub);
    _startSharedBotStatusStream();

    // Replay the last known payload so late subscribers render immediately
    // instead of waiting for the next server push.
    if (_botStatusLast != null) {
      try { onData(_botStatusLast); } catch { /* ignore */ }
    }

    return () => {
      _botStatusSubscribers = _botStatusSubscribers.filter((s) => s !== sub);
      if (_botStatusSubscribers.length === 0 && _botStatusCleanup) {
        try { _botStatusCleanup(); } catch { /* ignore */ }
        _botStatusCleanup = null;
        _botStatusLast = null;
        _botStatusTenantId = null;
      }
    };
  },

  streamBotStatus: (onData: (data: any) => void, onError?: (error: any) => void, providerId?: string) => {
    const token = sessionStorage.getItem('authToken');
    const selectedTenant = sessionStorage.getItem('selectedTenant');
    
    let url = `${API_BASE_URL}/bots/status/stream`;
    
    // Add tenant ID as URL parameter if needed
    if (selectedTenant) {
      try {
        const tenant = JSON.parse(selectedTenant);
        url += `?tenant_id=${encodeURIComponent(tenant.tenant_id)}`;
      } catch (error) {
        console.error('Failed to parse selected tenant:', error);
      }
    }

    if (providerId) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}provider_id=${encodeURIComponent(providerId)}`;
    }
    
    let xhr: XMLHttpRequest | null = null;
    let isClosed = false;
    let didRefresh = false;
    let reconnectTimer: number | null = null;
    let retryDelayMs = 1000;
    let requestSeq = 0;
    let activeRequestId = 0;
    let abortForReconnectRequestId: number | null = null;

    const clearReconnectTimer = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = (reason?: any) => {
      if (isClosed) return;
      clearReconnectTimer();
      // Avoid spamming UI: HTTP 0 is usually a disconnect/abort/CORS block.
      // We'll retry with backoff and only surface "real" HTTP codes.
      reconnectTimer = window.setTimeout(() => {
        openStream();
      }, retryDelayMs);
      retryDelayMs = Math.min(retryDelayMs * 2, 30000);
      // If caller wants visibility, pass the reason through once.
      if (reason) {
        onError?.(reason);
      }
    };

    const openStream = (accessToken?: string) => {
      if (isClosed) return;
      clearReconnectTimer();

      // Reset per-connection buffer
      let buffer = '';

      // Create a custom EventSource-like implementation that supports headers
      xhr = new XMLHttpRequest();
      const requestId = ++requestSeq;
      activeRequestId = requestId;
      xhr.open('GET', url, true);

      const effectiveToken = accessToken ?? token;

      // Add Authorization header
      if (effectiveToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${effectiveToken}`);
      }

      // Add tenant header if available
      if (selectedTenant) {
        try {
          const tenant = JSON.parse(selectedTenant);
          xhr.setRequestHeader('X-Tenant-ID', tenant.tenant_id);
        } catch (error) {
          console.error('Failed to parse selected tenant:', error);
        }
      }

      // Required by API gateway/proxy in production
      xhr.setRequestHeader('X-CAOSBLITZ-HEADER', CAOSBLITZ_HEADER);

      xhr.setRequestHeader('Accept', 'text/event-stream');
      xhr.setRequestHeader('Cache-Control', 'no-cache');

      xhr.onprogress = function() {
        if (!xhr) return;
        if (activeRequestId !== requestId) return;
        const newData = xhr.responseText.substring(buffer.length);
        buffer = xhr.responseText;

        const lines = newData.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              onData(data);
              // Received data: connection is alive; reset retry backoff.
              retryDelayMs = 1000;
            } catch (error) {
              console.error('Error parsing SSE data:', error);
              onError?.(error);
            }
          }
        }
      };

      xhr.onerror = function(error) {
        if (isClosed) return;
        if (activeRequestId !== requestId) return;
        // Network-level failure; status will often be 0.
        console.error('SSE error:', error);
        scheduleReconnect();
      };

      xhr.onreadystatechange = function() {
        if (!xhr) return;
        if (activeRequestId !== requestId) return;
        if (xhr.readyState !== XMLHttpRequest.DONE) return;
        if (isClosed) return;

        // Ignore abort() we intentionally triggered to reconnect.
        if (xhr.status === 0 && abortForReconnectRequestId === requestId) {
          abortForReconnectRequestId = null;
          return;
        }

        // Match axios behavior: one refresh+retry on 401
        if (xhr.status === 401 && !didRefresh && !isClosed) {
          didRefresh = true;
          void (async () => {
            try {
              const refreshResponse = await performTokenRefresh();
              sessionStorage.setItem('authToken', refreshResponse.access_token);
              sessionStorage.setItem('user', JSON.stringify(refreshResponse.user));
              try {
                abortForReconnectRequestId = requestId;
                xhr?.abort();
              } catch {}
              openStream(refreshResponse.access_token);
            } catch (refreshError) {
              clearAuthSession();
              window.location.href = '/login';
            }
          })();
          return;
        }

        // Status 0 is typical for disconnects / blocked requests; retry quietly.
        if (xhr.status === 0) {
          scheduleReconnect();
          return;
        }

        if (xhr.status !== 200) {
          console.error('SSE connection failed:', xhr.status, xhr.statusText);
          onError?.(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          // Also retry on transient failures.
          scheduleReconnect();
        }
      };

      xhr.send();
    };

    openStream();

    return () => {
      isClosed = true;
      clearReconnectTimer();
      try { xhr?.abort(); } catch {}
      xhr = null;
    };
  },

  streamOperationsEvents: (onData: (data: any) => void, onError?: (error: any) => void) => {
    const token = sessionStorage.getItem('authToken');
    const selectedTenant = sessionStorage.getItem('selectedTenant');
    let url = `${API_BASE_URL}/operations/events/stream`;
    if (selectedTenant) {
      try {
        const tenant = JSON.parse(selectedTenant);
        url += `?tenant_id=${encodeURIComponent(tenant.tenant_id)}`;
      } catch (error) {
        console.error('Failed to parse selected tenant:', error);
      }
    }

    let xhr: XMLHttpRequest | null = null;
    let isClosed = false;
    let didRefresh = false;

    const openStream = (accessToken?: string) => {
      if (isClosed) return;

      let buffer = '';
      xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);

      const effectiveToken = accessToken ?? token;
      if (effectiveToken) xhr.setRequestHeader('Authorization', `Bearer ${effectiveToken}`);
      if (selectedTenant) {
        try {
          const tenant = JSON.parse(selectedTenant);
          xhr.setRequestHeader('X-Tenant-ID', tenant.tenant_id);
        } catch {}
      }

      xhr.setRequestHeader('X-CAOSBLITZ-HEADER', CAOSBLITZ_HEADER);
      xhr.setRequestHeader('Accept', 'text/event-stream');
      xhr.setRequestHeader('Cache-Control', 'no-cache');

      xhr.onprogress = function() {
        if (!xhr) return;
        const newData = xhr.responseText.substring(buffer.length);
        buffer = xhr.responseText;
        const lines = newData.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try { onData(JSON.parse(line.substring(6))); }
            catch (e) { console.error('Error parsing operations SSE:', e); onError?.(e as any); }
          }
        }
      };
      xhr.onerror = function(error) { console.error('Ops SSE error:', error); onError?.(error); };
      xhr.onreadystatechange = function() {
        if (!xhr) return;
        if (xhr.readyState !== XMLHttpRequest.DONE) return;

        if (xhr.status === 401 && !didRefresh && !isClosed) {
          didRefresh = true;
          void (async () => {
            try {
              const refreshResponse = await performTokenRefresh();
              sessionStorage.setItem('authToken', refreshResponse.access_token);
              sessionStorage.setItem('user', JSON.stringify(refreshResponse.user));
              try { xhr?.abort(); } catch {}
              openStream(refreshResponse.access_token);
            } catch (refreshError) {
              clearAuthSession();
              window.location.href = '/login';
            }
          })();
          return;
        }

        if (xhr.status !== 200) {
          onError?.(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.send();
    };

    openStream();
    return () => {
      isClosed = true;
      try { xhr?.abort(); } catch {}
      xhr = null;
    };
  },

  // Optional: service-to-service publisher is backend-only; no frontend method needed

  getBotRequirements: async (): Promise<{ tenant_id: string; requirements: Array<{
    tenant_id: string;
    region_id: string;
    tenant_name: string;
    region_name: string;
    required_bots: number;
    created_bots: number;
    current_bots: number;
    pending_creation: number;
  }> }> => {
    const response = await axiosInstance.get('/bots/requirements');
    return response.data;
  },

  getStats: async (): Promise<BotStats> => {
    const response = await axiosInstance.get('/stats');
    return response.data;
  },

  syncAMI: async (): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/maintenance/sync-ami');
    return response.data;
  },

  clearTerminatingTransactions: async (): Promise<{ message: string; updated_count: number }> => {
    const response = await axiosInstance.post('/maintenance/clear-terminating-transactions');
    return response.data;
  },

  // Partner management
  getPartners: async (): Promise<Partner[]> => {
    const response = await axiosInstance.get('/partners');
    return response.data;
  },

  createPartner: async (request: PartnerCreateRequest): Promise<Partner> => {
    const response = await axiosInstance.post('/partners', request);
    return response.data;
  },

  updatePartner: async (partnerId: string, request: PartnerUpdateRequest): Promise<Partner> => {
    const response = await axiosInstance.put(`/partners/${partnerId}`, request);
    return response.data;
  },

  deletePartner: async (partnerId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/partners/${partnerId}`);
    return response.data;
  },

  // Tenant management
  getTenants: async (): Promise<Tenant[]> => {
    const response = await axiosInstance.get('/tenants');
    return response.data;
  },

  createTenant: async (request: TenantCreateRequest): Promise<Tenant> => {
    const response = await axiosInstance.post('/tenants', request);
    return response.data;
  },

  updateTenant: async (tenantId: string, request: TenantUpdateRequest): Promise<Tenant> => {
    const response = await axiosInstance.put(`/tenants/${tenantId}`, request);
    return response.data;
  },

  deleteTenant: async (tenantId: string, resourcesToDelete: string[]): Promise<{ message: string; results: any }> => {
    const response = await axiosInstance.delete(`/tenants/${tenantId}`, {
      data: { resources_to_delete: resourcesToDelete }
    });
    return response.data;
  },

  // Audit log
  getAuditLog: async (params: {
    tenant_id?: string | null;
    from_ts?: string;
    to_ts?: string;
    event_type?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<AuditLogEntry[]> => {
    const searchParams = new URLSearchParams();
    if (params.tenant_id != null && params.tenant_id !== '') searchParams.set('tenant_id', params.tenant_id);
    if (params.from_ts) searchParams.set('from_ts', params.from_ts);
    if (params.to_ts) searchParams.set('to_ts', params.to_ts);
    if (params.event_type) searchParams.set('event_type', params.event_type);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.offset != null) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const url = qs ? `/audit-log?${qs}` : '/audit-log';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getTenantResourceCounts: async (tenantId: string): Promise<TenantResourceCounts> => {
    const response = await axiosInstance.get(`/tenants/${tenantId}/resource-counts`);
    return response.data;
  },

  // User management endpoints
  getPartnerUsers: async (partnerId: string): Promise<any[]> => {
    const response = await axiosInstance.get(`/auth/partners/${partnerId}/users`);
    return response.data;
  },

  getTenantUsers: async (tenantId: string): Promise<any[]> => {
    const response = await axiosInstance.get(`/auth/tenants/${tenantId}/users`);
    return response.data;
  },

  getAllUsersWithPermissions: async (): Promise<UserWithPermissions[]> => {
    const response = await axiosInstance.get('/users/permissions');
    return response.data;
  },

  createUser: async (request: CreateUserRequest): Promise<User> => {
    const response = await axiosInstance.post('/users', request);
    return response.data;
  },

  inviteUserToPartner: async (partnerId: string, email: string, role: string): Promise<any> => {
    const response = await axiosInstance.post(`/auth/partners/${partnerId}/invite`, { email, role });
    return response.data;
  },

  inviteUserToTenant: async (tenantId: string, request: InviteUserRequest): Promise<any> => {
    const response = await axiosInstance.post(`/auth/tenants/${tenantId}/invite`, request);
    return response.data;
  },

  assignUserToTenant: async (tenantId: string, request: { email: string; role: string }): Promise<any> => {
    const response = await axiosInstance.post(`/tenants/${tenantId}/assign-user`, request);
    return response.data;
  },

  updateUserRole: async (resourceType: 'partner' | 'tenant', resourceId: string, userId: string, role: string): Promise<any> => {
    const endpoint = resourceType === 'partner' 
      ? `/auth/partners/${resourceId}/users/${userId}/role`
      : `/auth/tenants/${resourceId}/users/${userId}/role`;
    const response = await axiosInstance.put(endpoint, { role });
    return response.data;
  },

  removeUser: async (resourceType: 'partner' | 'tenant', resourceId: string, userId: string): Promise<any> => {
    const endpoint = resourceType === 'partner' 
      ? `/auth/partners/${resourceId}/users/${userId}`
      : `/auth/tenants/${resourceId}/users/${userId}`;
    const response = await axiosInstance.delete(endpoint);
    return response.data;
  },

  deleteUser: async (userId: string): Promise<any> => {
    const response = await axiosInstance.delete(`/users/${userId}`);
    return response.data;
  },

  // Generic HTTP methods for direct API calls
  get: async (url: string): Promise<any> => {
    const response = await axiosInstance.get(url);
    return response;
  },

  post: async (url: string, data?: any, config?: any): Promise<any> => {
    const response = await axiosInstance.post(url, data, config);
    return response;
  },

  put: async (url: string, data?: any): Promise<any> => {
    const response = await axiosInstance.put(url, data);
    return response;
  },

  delete: async (url: string): Promise<any> => {
    const response = await axiosInstance.delete(url);
    return response;
  },



  // Get all invitations
  getInvitations: async (): Promise<Invitation[]> => {
    const response = await axiosInstance.get('/auth/invitations');
    return response.data;
  },

  // Revoke (delete) an invitation
  revokeInvitation: async (invitationId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/auth/invitations/${invitationId}`);
    return response.data;
  },

  // Attack Classes
  getAttackClasses: async (): Promise<{ name: string; description: string }[]> => {
    const response = await axiosInstance.get('/attacks/classes');
    return response.data;
  },

  // Attack Types
  getAttackTypes: async (classId?: string, scope?: string): Promise<AttackType[]> => {
    let url = '/attacks/types';
    const params = new URLSearchParams();
    if (classId) params.append('class_id', classId);
    if (scope) params.append('scope', scope);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getAttackType: async (typeId: string): Promise<AttackType> => {
    const response = await axiosInstance.get(`/attacks/types/${typeId}`);
    return response.data;
  },

  createAttackType: async (request: AttackTypeCreateRequest): Promise<AttackType> => {
    const response = await axiosInstance.post('/attacks/types', request);
    return response.data;
  },

  updateAttackType: async (typeId: string, request: AttackTypeUpdateRequest): Promise<AttackType> => {
    const response = await axiosInstance.put(`/attacks/types/${typeId}`, request);
    return response.data;
  },

  deleteAttackType: async (typeId: string): Promise<void> => {
    await axiosInstance.delete(`/attacks/types/${typeId}`);
  },

  // Common Fields
  getCommonFields: async (): Promise<any[]> => {
    const response = await axiosInstance.get('/attacks/common-fields');
    return response.data;
  },

  getCommonField: async (fieldName: string): Promise<{ field_name: string; values: any[] }> => {
    const response = await axiosInstance.get(`/attacks/common-fields/${fieldName}`);
    return response.data;
  },

  // Scenarios
  getScenarios: async (runbookId?: string | null): Promise<Scenario[]> => {
    const params = new URLSearchParams();
    if (runbookId) params.append('runbook_id', runbookId);
    const qs = params.toString();
    const response = await axiosInstance.get(`/attacks/scenarios${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  createScenario: async (request: ScenarioCreateRequest): Promise<Scenario> => {
    const response = await axiosInstance.post('/attacks/scenarios', request);
    return response.data;
  },

  getScenario: async (scenarioId: string): Promise<Scenario> => {
    const response = await axiosInstance.get(`/attacks/scenarios/${scenarioId}`);
    return response.data;
  },

  updateScenario: async (scenarioId: string, request: ScenarioUpdateRequest): Promise<Scenario> => {
    const response = await axiosInstance.put(`/attacks/scenarios/${scenarioId}`, request);
    return response.data;
  },

  // Update active scenario config in S3 (real-time during attack)
  updateScenarioConfig: async (scenarioId: string, config: Record<string, any>): Promise<{ message: string; scenario_id: string; updated_at: string }> => {
    const response = await axiosInstance.patch(`/attacks/scenarios/${scenarioId}/config`, config);
    return response.data;
  },

  deleteScenario: async (scenarioId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/attacks/scenarios/${scenarioId}`);
    return response.data;
  },

  // Executions
  /**
   * List executions for the current tenant.
   *
   * `runbookId` scopes the result to runs launched under that runbook (CAO-179) — the
   * runbook detail view passes it so a scenario shared by several runbooks does not show
   * another runbook's runs, nor free (ad-hoc) ones. Omit it for the unscoped history.
   */
  getExecutions: async (scenarioId?: string, status?: string, runbookId?: string): Promise<Execution[]> => {
    let url = '/attacks/executions';
    const params = new URLSearchParams();
    if (scenarioId) params.append('scenario_id', scenarioId);
    if (status) params.append('status', status);
    if (runbookId) params.append('runbook_id', runbookId);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await axiosInstance.get(url);
    return response.data;
  },

  createExecution: async (request: ExecutionCreateRequest): Promise<Execution> => {
    const response = await axiosInstance.post('/attacks/executions', request);
    return response.data;
  },

  getExecution: async (executionId: string): Promise<Execution> => {
    const response = await axiosInstance.get(`/attacks/executions/${executionId}`);
    return response.data;
  },

  updateExecutionStatus: async (executionId: string, status: string): Promise<{ message: string }> => {
    const response = await axiosInstance.put(`/attacks/executions/${executionId}/status`, { status });
    return response.data;
  },

  updateExecutionImpactBots: async (executionId: string, impactBots: number | null): Promise<Execution> => {
    const response = await axiosInstance.patch(`/attacks/executions/${executionId}/impact-bots`, {
      impact_bots: impactBots,
    });
    return response.data;
  },

  getExecutionBotIps: async (executionId: string): Promise<ExecutionBotIp[]> => {
    const response = await axiosInstance.get(`/attacks/executions/${executionId}/bots`);
    return response.data;
  },

  // Chat-like manual log entries (CAO-184): a text note and/or a screenshot,
  // appended to execution_log as a 'note' event alongside system-generated events.
  addExecutionLogEntry: async (
    executionId: string,
    entry: { message?: string; screenshot?: File }
  ): Promise<Execution> => {
    const formData = new FormData();
    if (entry.message) formData.append('message', entry.message);
    if (entry.screenshot) formData.append('screenshot', entry.screenshot);
    const response = await axiosInstance.post(`/attacks/executions/${executionId}/log-entries`, formData);
    return response.data;
  },

  // Set Attack Scenario (upload to S3)
  /**
   * Activate a scenario. `runbookId` records the runbook the run belongs to (CAO-179);
   * omit it when activating outside a runbook, which leaves the run free (ad-hoc).
   */
  getWarmFleetOptions: async (scenarioId: string): Promise<{ configured: boolean; options: import('../types/providers').WarmHostOption[]; pools: import('../types/providers').WarmFleetPool[] }> => {
    const response = await axiosInstance.get('/container-fleet/warm-options', { params: { scenario_id: scenarioId } });
    return response.data;
  },
  setAttackScenario: async (scenarioId: string, runbookId?: string, warmHosts?: Record<string, number>): Promise<{ message: string; s3_path: string; scenario_id: string }> => {
    const query = runbookId ? `?runbook_id=${encodeURIComponent(runbookId)}` : '';
    const response = warmHosts === undefined
      ? await axiosInstance.post(`/attacks/scenarios/${scenarioId}/set${query}`)
      : await axiosInstance.post(`/attacks/scenarios/${scenarioId}/set${query}`, { warm_hosts: warmHosts });
    return response.data;
  },

  // Run Debug — provision a single bot for a scenario and open a terminal to it.
  startScenarioDebug: async (
    scenarioId: string,
    regionId?: string
  ): Promise<ScenarioDebugState> => {
    const response = await axiosInstance.post(
      `/attacks/scenarios/${scenarioId}/debug`,
      regionId ? { region_id: regionId } : {}
    );
    return response.data;
  },

  getScenarioDebug: async (scenarioId: string): Promise<ScenarioDebugState> => {
    const response = await axiosInstance.get(`/attacks/scenarios/${scenarioId}/debug`);
    return response.data;
  },

  stopScenarioDebug: async (scenarioId: string): Promise<{ message: string; terminated: number }> => {
    const response = await axiosInstance.delete(`/attacks/scenarios/${scenarioId}/debug`);
    return response.data;
  },

  // Test Run (CAO-156) — provision a single bot that runs an ad-hoc attack
  // configuration (not yet a saved scenario) exactly once and captures its output.
  startTestRun: async (params: {
    attack_type_id: string;
    configuration: Record<string, any>;
    max_rps?: number | null;
    max_bandwidth_bps?: number | null;
    max_pps?: number | null;
    region_id?: string;
  }): Promise<TestRunState> => {
    const response = await axiosInstance.post('/attacks/test-run', params);
    return response.data;
  },

  getTestRun: async (): Promise<TestRunState> => {
    const response = await axiosInstance.get('/attacks/test-run');
    return response.data;
  },

  stopTestRun: async (): Promise<{ message: string; terminated: number }> => {
    const response = await axiosInstance.delete('/attacks/test-run');
    return response.data;
  },

  // Check if scenario is set in S3
  checkScenarioStatus: async (scenarioId: string): Promise<{ exists: boolean; s3_path?: string; last_modified?: string }> => {
    const response = await axiosInstance.get(`/attacks/scenarios/${scenarioId}/status`);
    return response.data;
  },

  // Get scenario configuration from S3 with parsed details (for a specific scenario by ID)
  getScenarioS3Config: async (scenarioId: string): Promise<ScenarioS3ConfigPayload> => {
    const response = await axiosInstance.get(`/attacks/scenarios/${scenarioId}/s3-config`);
    return response.data;
  },

  // Get currently set scenario and its S3 config in one call (avoids duplicate current-scenario + s3-config requests)
  getCurrentScenario: async (): Promise<CurrentScenarioResponse | null> => {
    const response = await axiosInstance.get('/attacks/current-scenario');
    return response.data;
  },

  // Runbooks
  getRunbooks: async (): Promise<RunbookListItem[]> => {
    const response = await axiosInstance.get('/runbooks');
    return response.data;
  },

  getRunbook: async (runbookId: string): Promise<Runbook> => {
    const response = await axiosInstance.get(`/runbooks/${runbookId}`);
    return response.data;
  },

  createRunbook: async (request: RunbookCreateRequest): Promise<Runbook> => {
    const response = await axiosInstance.post('/runbooks', request);
    return response.data;
  },

  updateRunbook: async (runbookId: string, request: RunbookUpdateRequest): Promise<Runbook> => {
    const response = await axiosInstance.put(`/runbooks/${runbookId}`, request);
    return response.data;
  },

  deleteRunbook: async (runbookId: string): Promise<void> => {
    await axiosInstance.delete(`/runbooks/${runbookId}`);
  },

  duplicateRunbook: async (runbookId: string): Promise<Runbook> => {
    const response = await axiosInstance.post(`/runbooks/${runbookId}/duplicate`);
    return response.data;
  },

  reorderRunbooks: async (runbookIds: string[]): Promise<RunbookListItem[]> => {
    const response = await axiosInstance.put('/runbooks/reorder', { runbook_ids: runbookIds });
    return response.data;
  },

  // Slack (super-admin-only — used to populate the runbook channel selector)
  getSlackChannels: async (): Promise<SlackChannelsResponse> => {
    const response = await axiosInstance.get('/slack/channels');
    return response.data;
  },

  // End current scenario. impactBots, when provided, is recorded on the execution(s) being stopped.
  endScenario: async (impactBots?: number | null): Promise<{ message: string; details: any }> => {
    const response = await axiosInstance.post(
      '/attacks/end-scenario',
      impactBots !== undefined ? { impact_bots: impactBots } : undefined
    );
    return response.data;
  },

  // Bot Manager Errors
  getBotManagerErrors: async (limit: number = 50, tenant_id?: string): Promise<{
    id: string;
    bot_id?: string;
    tenant_id?: string;
    region_name?: string;
    action: string;
    error_message: string;
    details?: string;
    retry_count: number;
    status: string;
    created_at: string;
    last_attempt_at: string;
  }[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (tenant_id) params.append('tenant_id', tenant_id);
    
    const response = await axiosInstance.get(`/bots/errors?${params.toString()}`);
    return response.data;
  },

  // Transaction Errors
  getTransactionErrors: async (limit: number = 50, tenant_id?: string): Promise<{
    id: string;
    transaction_id: string;
    tenant_id?: string;
    scenario_id?: string;
    region_name?: string;
    /** What the transaction was attempting (scale_up, shutdown, …). */
    action?: string;
    error_message: string;
    details?: any;
    affected_bots?: number;
    status?: string;
    created_at: string;
  }[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (tenant_id) params.append('tenant_id', tenant_id);
    const response = await axiosInstance.get(`/transactions/errors?${params.toString()}`);
    return response.data;
  },

  // Allow List Management
  // Create allow list entry
  createAllowListEntry: async (tenantId: string, request: AllowListCreateRequest): Promise<AllowListEntry> => {
    const response = await axiosInstance.post(`/tenants/${tenantId}/allow-list`, request);
    return response.data;
  },

  bulkCreateAllowListEntries: async (
    tenantId: string,
    values: string[],
    reason?: string
  ): Promise<{ created: AllowListEntry[]; skipped: string[]; failed: { value: string; reason: string }[] }> => {
    const response = await axiosInstance.post(`/tenants/${tenantId}/allow-list/bulk`, { values, reason });
    return response.data;
  },

  // Get allow list entries for a tenant
  getAllowListEntries: async (
    tenantId: string,
    statusFilter?: string,
    typeFilter?: string,
    search?: string
  ): Promise<AllowListEntry[]> => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    if (typeFilter) params.append('type_filter', typeFilter);
    if (search) params.append('search', search);

    const response = await axiosInstance.get(`/tenants/${tenantId}/allow-list?${params.toString()}`);
    return response.data;
  },

  // Get pending allow list entries (super users only)
  getPendingAllowListEntries: async (): Promise<AllowListEntry[]> => {
    const response = await axiosInstance.get('/allow-list/pending');
    return response.data;
  },

  // Get all allow list entries (super users only)
  getAllAllowListEntries: async (
    statusFilter?: string,
    typeFilter?: string,
    search?: string
  ): Promise<AllowListEntry[]> => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    if (typeFilter) params.append('type_filter', typeFilter);
    if (search) params.append('search', search);

    const response = await axiosInstance.get(`/allow-list/all?${params.toString()}`);
    return response.data;
  },

  // Update allow list entry status (super users only)
  updateAllowListEntry: async (entryId: string, request: AllowListUpdateRequest): Promise<AllowListEntry> => {
    const response = await axiosInstance.put(`/allow-list/${entryId}`, request);
    return response.data;
  },

  // Bulk-update status for multiple allow list entries
  bulkUpdateAllowListEntries: async (
    entryIds: string[],
    status: string,
    reason?: string
  ): Promise<{ updated: string[]; failed: { id: string; reason: string }[] }> => {
    const response = await axiosInstance.post('/allow-list/bulk-update', { entry_ids: entryIds, status, reason });
    return response.data;
  },

  // Permanently delete a single allow list entry (requires manage_allow_list)
  deleteAllowListEntry: async (entryId: string): Promise<void> => {
    await axiosInstance.delete(`/allow-list/${entryId}`);
  },

  // Permanently delete multiple allow list entries in one request
  bulkDeleteAllowListEntries: async (entryIds: string[]): Promise<AllowListBulkDeleteResponse> => {
    const response = await axiosInstance.post('/allow-list/bulk-delete', { entry_ids: entryIds });
    return response.data;
  },


  // Get allow list statistics for a tenant
  getAllowListStats: async (tenantId: string): Promise<AllowListStats> => {
    const response = await axiosInstance.get(`/tenants/${tenantId}/allow-list/stats`);
    return response.data;
  },

  // Get overall allow list statistics (super users only)
  getOverallAllowListStats: async (): Promise<AllowListStats> => {
    const response = await axiosInstance.get(`/allow-list/stats/overall`);
    return response.data;
  },

  // Validate targets against allow list
  validateTargetsInAllowList: async (tenantId: string, targets: string[]): Promise<{
    tenant_id: string;
    validation_results: Array<{
      target: string;
      is_approved: boolean;
      status?: string; // 'approved', 'requested', or undefined (not in list)
      allow_list_entry?: {
        id: string;
        value: string;
        type: string;
        status?: string;
      };
    }>;
    all_approved: boolean;
  }> => {
    const response = await axiosInstance.post('/allow-list/validate-targets', {
      tenant_id: tenantId,
      targets: targets
    });
    return response.data;
  },

  // Assets / Discovery
  getAssets: async (search?: string): Promise<AssetEntry[]> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const response = await axiosInstance.get(`/assets?${params.toString()}`);
    return response.data;
  },

  getAssetDetail: async (allowListId: string): Promise<AssetDetailResponse> => {
    const response = await axiosInstance.get(`/assets/${allowListId}/detail`);
    return response.data;
  },

  getAssetDiscovery: async (allowListId: string): Promise<AssetDiscovery> => {
    const response = await axiosInstance.get(`/assets/${allowListId}/discovery`);
    return response.data;
  },

  updateAssetDiscovery: async (
    allowListId: string,
    request: AssetDiscoveryUpdateRequest
  ): Promise<AssetDiscovery> => {
    const response = await axiosInstance.put(`/assets/${allowListId}/discovery`, request);
    return response.data;
  },

  refreshAssetDiscovery: async (allowListId: string): Promise<AssetDiscovery> => {
    const response = await axiosInstance.post(`/assets/${allowListId}/discovery/refresh`);
    return response.data;
  },

  deleteAsset: async (allowListId: string): Promise<void> => {
    await axiosInstance.delete(`/assets/${allowListId}`);
  },

  // Provider Management
  getProviders: async (provider_type?: string, is_public?: boolean): Promise<Provider[]> => {
    const params = new URLSearchParams();
    if (provider_type) params.append('provider_type', provider_type);
    if (is_public !== undefined) params.append('is_public', is_public.toString());
    const response = await axiosInstance.get(`/providers/?${params.toString()}`);
    return response.data;
  },

  getProvider: async (providerId: string): Promise<Provider> => {
    const response = await axiosInstance.get(`/providers/${providerId}`);
    return response.data;
  },

  createProvider: async (data: ProviderCreateRequest): Promise<Provider> => {
    const response = await axiosInstance.post('/providers/', data);
    return response.data;
  },

  updateProvider: async (providerId: string, data: ProviderUpdateRequest): Promise<Provider> => {
    const response = await axiosInstance.put(`/providers/${providerId}`, data);
    return response.data;
  },

  deleteProvider: async (providerId: string, options?: { cascade?: boolean }): Promise<void> => {
    const cascade = options?.cascade === true;
    await axiosInstance.delete(`/providers/${providerId}` , { params: cascade ? { cascade: true } : undefined });
  },

  // Provider Errors (cloud-provider errors surfaced by workers)
  getRecentProviderErrors: async (
    status: string = 'active',
    limit: number = 200
  ): Promise<ProviderErrorsResponse> => {
    const params = new URLSearchParams({ status, limit: limit.toString() });
    const response = await axiosInstance.get(`/providers/errors/recent?${params.toString()}`);
    return response.data;
  },

  getProviderErrors: async (
    providerId: string,
    status: string = 'active',
    limit: number = 200
  ): Promise<ProviderError[]> => {
    const params = new URLSearchParams({ status, limit: limit.toString() });
    const response = await axiosInstance.get(`/providers/${providerId}/errors?${params.toString()}`);
    return response.data;
  },

  acknowledgeProviderError: async (errorId: string): Promise<ProviderError> => {
    const response = await axiosInstance.post(`/providers/errors/${errorId}/acknowledge`);
    return response.data;
  },

  acknowledgeAllProviderErrors: async (
    providerId: string
  ): Promise<{ provider_id: string; acknowledged: number }> => {
    const response = await axiosInstance.post(`/providers/${providerId}/errors/acknowledge`);
    return response.data;
  },

  deleteProviderErrors: async (
    providerId: string,
    errorIds: string[]
  ): Promise<{ provider_id: string; deleted: number }> => {
    const response = await axiosInstance.post(`/providers/${providerId}/errors/delete`, {
      error_ids: errorIds,
    });
    return response.data;
  },

  requestInstanceCatalog: async (providerId: string, regionCode: string, refresh = false): Promise<InstanceCatalog> => {
    const response = await axiosInstance.post(`/providers/${providerId}/instance-catalog`, null, { params: { region_code: regionCode, refresh } });
    return response.data;
  },
  getInstanceCatalog: async (providerId: string, regionCode: string): Promise<InstanceCatalog> => {
    const response = await axiosInstance.get(`/providers/${providerId}/instance-catalog`, { params: { region_code: regionCode } });
    return response.data;
  },
  // Region Management
  getProviderRegions: async (providerId: string): Promise<Region[]> => {
    const response = await axiosInstance.get(`/providers/${providerId}/regions`);
    return response.data;
  },

  createRegion: async (providerId: string, request: RegionCreateRequest): Promise<Region> => {
    const response = await axiosInstance.post(`/providers/${providerId}/regions`, request);
    return response.data;
  },

  updateRegion: async (providerId: string, regionId: string, request: RegionUpdateRequest): Promise<Region> => {
    const response = await axiosInstance.put(`/providers/${providerId}/regions/${regionId}`, request);
    return response.data;
  },

  deleteRegion: async (providerId: string, regionId: string): Promise<void> => {
    await axiosInstance.delete(`/providers/${providerId}/regions/${regionId}`);
  },

  // Image Management
  getProviderRegionImages: async (providerId: string): Promise<RegionImageInfo[]> => {
    const response = await axiosInstance.get(`/providers/${providerId}/regions/images`);
    return response.data;
  },

  queryProviderImages: async (providerId: string, request: ImageSyncRequest): Promise<ImageSyncResponse> => {
    const response = await axiosInstance.post(`/providers/${providerId}/regions/images/query`, request);
    return response.data;
  },

  updateProviderImages: async (providerId: string, request: ImageUpdateBulkRequest): Promise<ImageUpdateResponse> => {
    const response = await axiosInstance.put(`/providers/${providerId}/regions/images`, request);
    return response.data;
  },

  // Supported Instance Types
  getSupportedInstanceTypes: async (providerType?: string): Promise<SupportedInstanceType[]> => {
    const url = providerType 
      ? `/providers/supported-instance-types/${providerType}`
      : '/providers/supported-instance-types';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getSupportedProviders: async (): Promise<SupportedProvider[]> => {
    const response = await axiosInstance.get('/providers/supported');
    return response.data;
  },

  // Region Capacity Management
  getProviderRegionsCapacity: async (providerId: string): Promise<RegionCapacity[]> => {
    const response = await axiosInstance.get(`/providers/${providerId}/regions/capacity`);
    return response.data;
  },

  syncProviderCapacity: async (providerId: string): Promise<CapacitySyncResponse> => {
    const response = await axiosInstance.post(`/providers/${providerId}/sync-capacity`);
    return response.data;
  },

  // Region discovery is delegated to the workers over Redis (the backend cannot call the
  // provider SDK). Start enqueues a discovery operation; poll the operation for the result +
  // add/disable preview; apply reconciles the region table once the operator confirms.
  startProviderRegionSync: async (providerId: string): Promise<RegionSyncStartResponse> => {
    const response = await axiosInstance.post(`/providers/${providerId}/sync-regions`);
    return response.data;
  },

  getProviderRegionSyncOperation: async (
    providerId: string,
    operationId: string,
  ): Promise<RegionSyncOperationResponse> => {
    const response = await axiosInstance.get(
      `/providers/${providerId}/sync-regions/operations/${operationId}`,
    );
    return response.data;
  },

  applyProviderRegionSync: async (
    providerId: string,
    operationId: string,
    regions?: string[],
  ): Promise<RegionSyncApplyResponse> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/sync-regions/operations/${operationId}/apply`,
      regions !== undefined ? { regions } : undefined,
    );
    return response.data;
  },

  getProviderCapacitySyncJob: async (providerId: string, jobId: string): Promise<CapacitySyncResponse> => {
    const response = await axiosInstance.get(`/providers/${providerId}/sync-capacity/${jobId}`);
    return response.data;
  },

  getActiveProviderCapacitySyncJob: async (providerId: string): Promise<CapacitySyncResponse | null> => {
    const response = await axiosInstance.get(`/providers/${providerId}/sync-capacity/active`, {
      validateStatus: (status) => status === 200 || status === 204,
    });
    return response.status === 204 ? null : response.data;
  },

  cancelProviderCapacitySync: async (providerId: string): Promise<{ message: string; job_id: string }> => {
    const response = await axiosInstance.post(`/providers/${providerId}/sync-capacity/cancel`);
    return response.data;
  },

  updateRegionManualLimit: async (
    providerId: string,
    regionId: string,
    manualBotLimit: number | null
  ): Promise<ManualLimitUpdateResponse> => {
    const response = await axiosInstance.put(
      `/providers/${providerId}/regions/${regionId}/capacity`,
      { manual_bot_limit: manualBotLimit }
    );
    return response.data;
  },

  setRegionCooldown: async (
    providerId: string,
    regionId: string,
    reason: string,
    durationMinutes: number = 30
  ): Promise<CooldownResponse> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/cooldown?reason=${encodeURIComponent(reason)}&duration_minutes=${durationMinutes}`
    );
    return response.data;
  },

  requestRegionQuotaIncrease: async (
    providerId: string,
    regionId: string,
    multiplier: number = 1.5,
    dimensions?: QuotaIncreaseDimension[]
  ): Promise<QuotaIncreaseResponse> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/quota-increase`,
      { multiplier, dimensions }
    );
    return response.data;
  },

  listAzureReservations: async (
    providerId: string,
    regionId: string
  ): Promise<AzureReservationSummary> => {
    const response = await axiosInstance.get(
      `/providers/${providerId}/regions/${regionId}/reservations`
    );
    return response.data;
  },

  // Batched: one request returns a reservation summary for every region of the provider.
  // Preferred over fanning out listAzureReservations per region (which drained the DB pool).
  listAzureProviderReservations: async (
    providerId: string
  ): Promise<AzureReservationSummary[]> => {
    const response = await axiosInstance.get(
      `/providers/${providerId}/reservations`
    );
    return response.data;
  },

  reserveAzureReservations: async (
    providerId: string,
    regionId: string,
    count: number,
    includeIpv6: boolean
  ): Promise<AzureReservationSummary> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/reservations/reserve`,
      { count, include_ipv6: includeIpv6 }
    );
    return response.data;
  },

  scaleDownAzureReservations: async (
    providerId: string,
    regionId: string,
    count: number
  ): Promise<AzureReservationSummary> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/reservations/scale-down`,
      { count, include_ipv6: false }
    );
    return response.data;
  },

  releaseAzureReservations: async (
    providerId: string,
    regionId: string,
    count?: number,
    reservationIds?: string[]
  ): Promise<AzureReservationSummary> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/reservations/release`,
      { count, reservation_ids: reservationIds }
    );
    return response.data;
  },

  reclaimAzureReservations: async (
    providerId: string,
    regionId: string
  ): Promise<AzureReservationSummary> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/regions/${regionId}/reservations/reclaim`
    );
    return response.data;
  },

  getAzureReservationOperation: async (
    providerId: string,
    regionId: string,
    operationId: string
  ): Promise<AzureReservationOperation> => {
    const response = await axiosInstance.get(
      `/providers/${providerId}/regions/${regionId}/reservations/operations/${operationId}`
    );
    return response.data;
  },

  // Enqueue a reserve op for many regions in one request (bulk reserve). Each region still becomes
  // its own operation; only the API round-trips collapse from N to 1. Per-region errors ride back
  // inline in `results`.
  reserveProviderReservationsBatch: async (
    providerId: string,
    items: { region_id: string; count: number; include_ipv6: boolean; machine_type?: string }[]
  ): Promise<{ results: Array<{ region_id: string; operation_id?: string; operation_status?: string; error?: string }> }> => {
    const response = await axiosInstance.post(
      `/providers/${providerId}/reservations/reserve-batch`,
      { items }
    );
    return response.data;
  },

  // Poll statuses for a whole group of reservation operations in one request (bulk reserve group
  // poller), instead of one poll per region.
  getProviderReservationOperationsBatch: async (
    providerId: string,
    ids: string[]
  ): Promise<AzureReservationOperation[]> => {
    const response = await axiosInstance.get(
      `/providers/${providerId}/reservations/operations`,
      { params: { ids: ids.join(',') } }
    );
    return response.data;
  },

  queryPrometheusRange: async (
    query: string,
    start: number,
    end: number,
    step: number = 15
  ): Promise<any> => {
    const response = await axiosInstance.get('/metrics/prometheus/query_range', {
      params: { query, start, end, step }
    });
    return response.data;
  },

  queryPrometheusInstant: async (query: string, timeSec?: number): Promise<any> => {
    const response = await axiosInstance.get('/metrics/prometheus/query', {
      params: timeSec == null ? { query } : { query, time: timeSec }
    });
    return response.data;
  },

  queryPrometheusInstantBatch: async (queries: string[]): Promise<any> => {
    const response = await axiosInstance.post('/metrics/prometheus/query_batch', {
      queries
    });
    return response.data;
  },

  // Monitor targets -- extra probe URLs attached to the active execution
  getMonitorTargets: async (): Promise<{ scenario_targets: string[]; monitor_targets: string[]; paused: boolean }> => {
    const response = await axiosInstance.get('/attacks/monitor-targets');
    return response.data;
  },

  setMonitorTargets: async (targets: string[]): Promise<{ scenario_targets: string[]; monitor_targets: string[]; paused: boolean }> => {
    const response = await axiosInstance.put('/attacks/monitor-targets', { targets });
    return response.data;
  },

  setMonitorPaused: async (paused: boolean): Promise<{ scenario_targets: string[]; monitor_targets: string[]; paused: boolean }> => {
    const response = await axiosInstance.put('/attacks/monitor-pause', { paused });
    return response.data;
  },

  // ── Monitor Unit (cross-account EventBridge probe infrastructure) ──────────

  /** Generate a fresh CloudFormation template + ExternalId for a new monitor unit. */
  getMonitorUnitCfTemplate: async (): Promise<{
    external_id: string;
    yaml: string;
    aws_cli_command: string;
    launch_stack_url: string;
  }> => {
    const response = await axiosInstance.get('/monitor-units/cf-template');
    return response.data;
  },

  /**
   * Provisioned regions of the active monitor unit.
   * Available to any authenticated user (the dashboard monitoring panel needs it);
   * the full listMonitorUnits() listing is super-admin only.
   */
  getActiveMonitorRegions: async (): Promise<{
    monitor_unit_id: string | null;
    status: string | null;
    provisioned_regions: string[];
  }> => {
    const response = await axiosInstance.get('/monitor-units/active-regions');
    return response.data;
  },

  /** List all monitor units. */
  listMonitorUnits: async (): Promise<MonitorUnitData[]> => {
    const response = await axiosInstance.get('/monitor-units');
    return response.data;
  },

  /** Create / register a new monitor unit. */
  createMonitorUnit: async (payload: {
    name?: string;
    aws_account_id: string;
    role_arn: string;
    external_id: string;
    selected_regions: string[];
  }): Promise<MonitorUnitData> => {
    const response = await axiosInstance.post('/monitor-units', payload);
    return response.data;
  },

  /** Set a monitor unit as the active one used for execution scheduling. */
  activateMonitorUnit: async (id: string): Promise<MonitorUnitData> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/activate`);
    return response.data;
  },

  /** Rename a monitor unit. */
  renameMonitorUnit: async (id: string, name: string): Promise<MonitorUnitData> => {
    const response = await axiosInstance.put(`/monitor-units/${id}/name`, { name });
    return response.data;
  },

  /** Update the selected regions for a monitor unit. */
  updateMonitorUnitRegions: async (id: string, regions: string[]): Promise<MonitorUnitData> => {
    const response = await axiosInstance.put(`/monitor-units/${id}/regions`, { selected_regions: regions });
    return response.data;
  },

  /** Validate that CaosBlitz can access the customer AWS account via the role. */
  validateMonitorUnit: async (id: string): Promise<{
    success: boolean;
    details: string;
    assumed_account_id?: string;
  }> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/validate`);
    return response.data;
  },

  /** Kick off async Lambda + EventBridge provisioning in the customer account. */
  provisionMonitorUnit: async (id: string): Promise<MonitorUnitData> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/provision`);
    return response.data;
  },

  /** Poll provisioning progress. */
  getMonitorUnitProvisionStatus: async (id: string): Promise<MonitorUnitData> => {
    const response = await axiosInstance.get(`/monitor-units/${id}/provision-status`);
    return response.data;
  },

  /** Delete all remote AWS resources and remove the monitor unit record. */
  decommissionMonitorUnit: async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete(`/monitor-units/${id}`);
    return response.data;
  },

  /** Push current OTEL env vars to all deployed Lambda functions in this monitor unit. */
  syncMonitorUnitLambdaEnv: async (id: string): Promise<{ updated: string[]; failed: any[] }> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/sync-lambda-env`);
    return response.data;
  },

  /** Check if a newer Lambda code version is available on S3. */
  checkMonitorUnitCodeUpdate: async (id: string): Promise<{ update_available: boolean; deployed_hash: string | null; s3_hash: string | null }> => {
    const response = await axiosInstance.get(`/monitor-units/${id}/check-code-update`);
    return response.data;
  },

  /** Push the latest probe-runner code from S3 to all provisioned Lambda functions. */
  updateMonitorUnitLambdaCode: async (id: string): Promise<{ updated: string[]; failed: any[]; code_hash: string }> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/update-lambda-code`);
    return response.data;
  },

  /** Create EventBridge schedules for any currently-running executions. */
  syncMonitorUnitSchedules: async (id: string): Promise<{ message: string; scheduled: any[] }> => {
    const response = await axiosInstance.post(`/monitor-units/${id}/sync-schedules`);
    return response.data;
  },
};

// Export allow list types
export type {
  AllowListEntry,
  AllowListCreateRequest,
  AllowListUpdateRequest,
  AllowListStats,
  AllowListBulkDeleteResponse
} from '../types/allowList';

export type {
  AssetEntry,
  AssetDiscovery,
  AssetDiscoveryUpdateRequest,
  AssetDetailResponse,
  AssetDetailAttackType,
  AssetDetailExecution,
  AssetDetailRunbook,
} from '../types/assets';

// Export runbook types
export type {
  Runbook,
  RunbookListItem,
  RunbookCreateRequest,
  RunbookUpdateRequest,
  RunbookScenarioRef,
  RunbookScenarioDetail
} from '../types/runbooks';

// Monitor Unit (mirrors backend MonitorUnit model)
export interface MonitorUnitData {
  id: string;
  name: string | null;
  is_active: boolean;
  aws_account_id: string;
  role_arn: string;
  external_id: string;
  /** pending | validated | provisioning | active | failed | decommissioned */
  status: string;
  selected_regions: string[];
  provisioned_regions: string[] | null;
  provision_log: Record<string, { status: string; error?: string; timestamp?: string }> | null;
  lambda_function_name: string;
  scheduler_group_name: string | null;
  deployed_code_hash: string | null;
  created_at: string;
  updated_at: string;
  validated_at: string | null;
  provisioned_at: string | null;
}

export default api;
